import React, { useState } from 'react';
import { Product, ProductOrderItem } from '../../types/products';
import ProductVariantsModal from './modals/ProductVariantsModal';
import './orders.css';

interface ProductCardProps {
  product: Product;
  onAddToOrder: (orderItem: ProductOrderItem) => void;
  disabled?: boolean;
  /** Units of this product already in the pending (unsent) order — shown as a corner badge. */
  quantityInCart?: number;
  compact?: boolean; // Kept for compatibility, but not used in new design
}

const ProductCard: React.FC<ProductCardProps> = ({
  product,
  onAddToOrder,
  disabled = false,
  quantityInCart = 0,
}) => {
  const [showVariantsModal, setShowVariantsModal] = useState(false);
  const isUnavailable = !product.isAvailable;

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(price);
  };

  // Whole card is the tap target: quick-add, or open the variants modal
  // when the product requires choices. Optional customization (notes,
  // optional variants) lives in the cart's "Editar" action after adding.
  const handleCardClick = () => {
    if (isUnavailable || disabled) return;
    if (product.hasRequiredVariantGroups) {
      setShowVariantsModal(true);
      return;
    }
    const orderItem: ProductOrderItem = {
      productId: product.id,
      productName: product.name,
      quantity: 1,
      selectedVariants: [],
      unitPrice: product.price,
      productionCenters: product.productionCenters,
    };
    onAddToOrder(orderItem);
  };

  const handleVariantsConfirm = (orderItem: ProductOrderItem) => {
    onAddToOrder(orderItem);
  };

  return (
    <>
      <button
        type="button"
        className={`product-card-tile ${isUnavailable || disabled ? 'disabled' : ''} ${quantityInCart > 0 ? 'in-cart' : ''}`}
        onClick={handleCardClick}
        disabled={isUnavailable || disabled}
        aria-label={`Agregar ${product.name}`}
      >
        {quantityInCart > 0 && (
          <span className="product-tile-badge">{quantityInCart}</span>
        )}

        {product.imageUrl && (
          <img
            src={product.imageUrl}
            alt=""
            className="product-tile-image"
          />
        )}

        <span className="product-tile-name">{product.name}</span>

        {product.description && (
          <span className="product-tile-description">{product.description}</span>
        )}

        <span className="product-tile-footer">
          <span className="product-tile-price">{formatPrice(product.price)}</span>
          {isUnavailable ? (
            <span className="product-tile-tag unavailable">Agotado</span>
          ) : product.hasRequiredVariantGroups ? (
            <span className="product-tile-tag">opciones</span>
          ) : null}
        </span>
      </button>

      <ProductVariantsModal
        visible={showVariantsModal}
        onCancel={() => setShowVariantsModal(false)}
        onConfirm={handleVariantsConfirm}
        product={product}
      />
    </>
  );
};

export default ProductCard;
